import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit, getDoc, doc, where, getFirestore } from 'firebase/firestore';
import { db, archiveUserAccount, sendAppEmailCallable } from '../../../services/firebase';
import CandidateProgressTable from './CandidateProgressTable';
import CandidateDetailView from './CandidateDetailView';
import { toast } from 'react-hot-toast';
import { getFunctions, httpsCallable } from 'firebase/functions';

export default function CandidateProgressView() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [loadingUserDetail, setLoadingUserDetail] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [archiving, setArchiving] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [sendingWelcomeEmails, setSendingWelcomeEmails] = useState(false);
  const [showArchivedUsers, setShowArchivedUsers] = useState(false);

  // Function to refresh candidates data
  const fetchCandidates = async () => {
    try {
      setLoading(true);
      
      // Fetch all user profiles without requiring lastLogin field
      let userDocs = [];
      
      try {
        // Single query to get all user profiles
        const usersQuery = query(
          collection(db, 'user_profiles'),
          limit(500) // Get all users in one query
        );
        
        const userSnapshots = await getDocs(usersQuery);
        userDocs = userSnapshots.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log(`Fetched ${userDocs.length} total users`);
        
        // Debug logging for the specific user mentioned
        const debugUserId = 'PqFv4fUFhrbBAFKQb1RgfZpoqMY2';
        const debugUser = userDocs.find(user => user.id === debugUserId);
        if (debugUser) {
          console.log('Found debug user:', debugUserId, {
            hasLastLogin: !!debugUser.lastLogin,
            lastLoginType: typeof debugUser.lastLogin,
            lastLoginValue: debugUser.lastLogin,
            isArchived: debugUser.isArchived,
            email: debugUser.email || debugUser.basicInfo?.email
          });
        } else {
          console.log('Debug user not found in fetched results:', debugUserId);
        }
        
      } catch (error) {
        console.error('Error fetching user profiles:', error);
        toast.error('Failed to load candidates');
        return;
      }
      
      // Filter out archived users only if showArchivedUsers is false
      if (!showArchivedUsers) {
        userDocs = userDocs.filter(user => user.isArchived !== true);
      }
      
      // Debug logging after filtering
      const debugUserIdAfterFilter = 'PqFv4fUFhrbBAFKQb1RgfZpoqMY2';
      const debugUserAfterFilter = userDocs.find(user => user.id === debugUserIdAfterFilter);
      console.log('Debug user after archived filter:', !!debugUserAfterFilter, 'showArchivedUsers:', showArchivedUsers);
      
      // Sort users in JavaScript - users with lastLogin first (by most recent), then users without lastLogin
      userDocs.sort((a, b) => {
        const aHasLastLogin = a.lastLogin && a.lastLogin.toDate;
        const bHasLastLogin = b.lastLogin && b.lastLogin.toDate;
        
        // If both have lastLogin, sort by most recent
        if (aHasLastLogin && bHasLastLogin) {
          return b.lastLogin.toDate() - a.lastLogin.toDate();
        }
        
        // Users with lastLogin come first
        if (aHasLastLogin && !bHasLastLogin) return -1;
        if (!aHasLastLogin && bHasLastLogin) return 1;
        
        // For users without lastLogin, sort by createdAt if available
        if (a.createdAt && b.createdAt) {
          const aCreated = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const bCreated = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return bCreated - aCreated; // Most recent first
        }
        
        // If no createdAt, sort alphabetically by display name or email
        const aName = a.displayName || a.email || '';
        const bName = b.displayName || b.email || '';
        return aName.localeCompare(bName);
      });
      
      // Helper function to extract name from resume data
      const extractNameFromResume = (user) => {
        if (user.resumeExtractedText && typeof user.resumeExtractedText === 'string') {
          // Look for name patterns in the resume text
          const text = user.resumeExtractedText;
          
          // Try to find name in resume filename first
          if (user.resumeName && typeof user.resumeName === 'string') {
            const nameMatch = user.resumeName.match(/^([A-Za-z\s]+?)(?:\s+Resume|\s+CV|\.pdf|\.doc)/i);
            if (nameMatch && nameMatch[1].trim().length > 2) {
              return nameMatch[1].trim();
            }
          }
          
          // Look for email patterns and extract name from them
          const emailMatch = text.match(/([A-Za-z]+[A-Za-z\s]*[A-Za-z]+)\s*[:\-]?\s*[\w\.-]+@[\w\.-]+\.\w+/);
          if (emailMatch && emailMatch[1]) {
            const potentialName = emailMatch[1].trim();
            // Validate it looks like a name (2+ words, reasonable length)
            if (potentialName.split(' ').length >= 2 && potentialName.length <= 50) {
              return potentialName;
            }
          }
          
          // Look for capitalized name patterns at the beginning of the resume
          const lines = text.split('\n').slice(0, 5); // Check first 5 lines
          for (const line of lines) {
            const cleanLine = line.trim();
            // Look for patterns like "First Last" at the start
            const namePattern = /^([A-Z][a-z]+(?:\s+[A-Z][a-z]*){1,3})\s*$/;
            const match = cleanLine.match(namePattern);
            if (match && match[1] && match[1].length >= 4 && match[1].length <= 50) {
              return match[1];
            }
          }
        }
        
        return null;
      };
      
      // Fetch additional data for each user (similar to the original useEffect)
      const enrichedCandidates = await Promise.all(
        userDocs.map(async (user) => {
          // Improve name resolution - try multiple sources
          let displayName = null;
          
          // First check existing displayName and basicInfo
          if (user.basicInfo?.firstName && user.basicInfo?.lastName) {
            displayName = `${user.basicInfo.firstName} ${user.basicInfo.lastName}`;
          } else if (user.basicInfo?.name) {
            displayName = user.basicInfo.name;
          } else if (user.displayName) {
            if (typeof user.displayName === 'object' && user.displayName.text) {
              displayName = user.displayName.text;
            } else if (typeof user.displayName === 'string') {
              displayName = user.displayName;
            }
          }
          
          // If no name found, try to extract from resume data
          if (!displayName) {
            const resumeName = extractNameFromResume(user);
            if (resumeName) {
              displayName = resumeName;
              console.log(`Extracted name "${resumeName}" from resume for user ${user.id}`);
            }
          }
          
          // If still no name, use email as fallback
          if (!displayName) {
            const email = user.basicInfo?.email || user.email;
            if (email) {
              const emailName = email.split('@')[0];
              displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
            } else {
              displayName = 'Unknown User';
            }
          }
          
          // Fetch profile summary if it exists
          let profileSummary = null;
          try {
            const summaryDoc = await getDoc(doc(db, 'user_profile_summaries', user.id));
            if (summaryDoc.exists()) {
              profileSummary = summaryDoc.data();
            }
          } catch (error) {
            console.error('Error fetching profile summary:', error);
          }
          
          // Find company connections (if any)
          let companyConnection = null;
          if (user.companyStatuses) {
            // Find company IDs with "introduction-requested" status
            const companyIds = Object.entries(user.companyStatuses)
              .filter(([_, status]) => status === 'introduction-requested')
              .map(([id]) => id);
            
            // Fetch the most recent company data
            if (companyIds.length > 0) {
              try {
                const mostRecentCompanyId = companyIds[0]; // Can be improved to find the most recent one
                const companyDoc = await getDoc(doc(db, 'companies', mostRecentCompanyId));
                
                if (companyDoc.exists()) {
                  const companyData = companyDoc.data();
                  const timestamp = user.companyStatusHistory?.[mostRecentCompanyId]?.[0]?.timestamp || null;
                  
                  companyConnection = {
                    id: mostRecentCompanyId,
                    name: companyData.name,
                    logo: companyData.logo,
                    timestamp: timestamp
                  };
                }
              } catch (error) {
                console.error('Error fetching company data:', error);
              }
            }
          }
          
          return {
            ...user,
            displayName, // Use our improved displayName
            profileSummary,
            companyConnection
          };
        })
      );
      
      setCandidates(enrichedCandidates);
      
      // Debug logging for final results
      const debugUserId = 'PqFv4fUFhrbBAFKQb1RgfZpoqMY2';
      const debugUserInFinal = enrichedCandidates.find(user => user.id === debugUserId);
      console.log(`Final candidates count: ${enrichedCandidates.length}`);
      console.log('Debug user in final candidates:', !!debugUserInFinal);
      if (debugUserInFinal) {
        console.log('Debug user final data:', {
          displayName: debugUserInFinal.displayName,
          email: debugUserInFinal.email,
          isArchived: debugUserInFinal.isArchived
        });
      }
      
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, []);

  // Refresh candidates when showArchivedUsers changes
  useEffect(() => {
    fetchCandidates();
  }, [showArchivedUsers]);

  // Add event listener for refreshing candidate data
  useEffect(() => {
    const handleRefreshCandidateData = (event) => {
      const { userId } = event.detail;
      
      // If this is the currently selected user, refresh the data
      if (userId === selectedUserId) {
        console.log('Refreshing candidate data for', userId);
        handleCandidateClick(userId);
      }
    };
    
    window.addEventListener('refresh-candidate-data', handleRefreshCandidateData);
    
    return () => {
      window.removeEventListener('refresh-candidate-data', handleRefreshCandidateData);
    };
  }, [selectedUserId]);

  // Handle user selection with comprehensive data fetching
  const handleCandidateClick = async (userId) => {
    setSelectedUserId(userId);
    setLoadingUserDetail(true);
    
    try {
      const db = getFirestore();
      
      // First check if there's a user profile in user_profiles collection
      console.log("Checking for user profile in user_profiles collection");
      const userProfileRef = doc(db, 'user_profiles', userId);
      const userProfileSnap = await getDoc(userProfileRef);
      
      let userProfileData = null;
      let profileSummaryData = null;
      let waitlistData = null;
      let submissionId = null;
      
      if (userProfileSnap.exists()) {
        console.log("Found user profile:", userProfileSnap.data());
        userProfileData = userProfileSnap.data();
      }
      
      // Always check for profile summary data
      console.log("Checking for profile summary data");
      const profileSummaryRef = doc(db, 'user_profile_summaries', userId);
      const profileSummarySnap = await getDoc(profileSummaryRef);
      
      if (profileSummarySnap.exists()) {
        console.log("Found profile summary:", profileSummarySnap.data());
        profileSummaryData = profileSummarySnap.data();
      }
      
      // Check waitlist data
      if (userProfileData && userProfileData.waitlistSubmissionId) {
        submissionId = userProfileData.waitlistSubmissionId;
        const waitlistRef = doc(db, 'waitlist', submissionId);
        const waitlistSnap = await getDoc(waitlistRef);
        
        if (waitlistSnap.exists()) {
          waitlistData = waitlistSnap.data();
          console.log("Found waitlist data from user profile reference:", waitlistData);
        }
      } else {
        // If no waitlist submission ID in user profile, try to find by user ID
        const waitlistRef = collection(db, 'waitlist');
        const q = query(waitlistRef, where('userId', '==', userId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          waitlistData = querySnapshot.docs[0].data();
          submissionId = querySnapshot.docs[0].id;
          console.log("Found waitlist data by user ID query:", waitlistData);
        }
      }
      
      // Try to create a proper display name using multiple sources (including old structures)
      let displayName = 'Name not available';
      
      if (userProfileData?.basicInfo?.firstName && userProfileData?.basicInfo?.lastName) {
        // Current structure: nested in basicInfo
        displayName = `${userProfileData.basicInfo.firstName} ${userProfileData.basicInfo.lastName}`;
      } else if (userProfileData?.firstName && userProfileData?.lastName) {
        // Old structure: top-level firstName/lastName
        displayName = `${userProfileData.firstName} ${userProfileData.lastName}`;
      } else if (userProfileData?.basicInfo?.name) {
        displayName = userProfileData.basicInfo.name;
      } else if (userProfileData?.name) {
        // Old structure: top-level name
        displayName = userProfileData.name;
      } else if (userProfileData?.displayName) {
        // Check if displayName is an object
        if (userProfileData.displayName === null) {
          displayName = 'Name not available';
        } else if (typeof userProfileData.displayName === 'object' && userProfileData.displayName.text) {
          displayName = userProfileData.displayName.text;
        } else {
          displayName = userProfileData.displayName;
        }
      } else if (waitlistData?.userInfo?.firstName && waitlistData?.userInfo?.lastName) {
        displayName = `${waitlistData.userInfo.firstName} ${waitlistData.userInfo.lastName}`;
      } else if (waitlistData?.firstName && waitlistData?.lastName) {
        // Old structure: top-level firstName/lastName in waitlist
        displayName = `${waitlistData.firstName} ${waitlistData.lastName}`;
      } else if (waitlistData?.userInfo?.name) {
        displayName = waitlistData.userInfo.name;
      } else if (waitlistData?.name) {
        // Old structure: top-level name in waitlist
        displayName = waitlistData.name;
      } else {
        // Try to extract name from resume data if no other name is available
        if (userProfileData?.resumeName && typeof userProfileData.resumeName === 'string') {
          const nameMatch = userProfileData.resumeName.match(/^([A-Za-z\s]+?)(?:\s+Resume|\s+CV|\.pdf|\.doc)/i);
          if (nameMatch && nameMatch[1].trim().length > 2) {
            displayName = nameMatch[1].trim();
            console.log(`Extracted name "${displayName}" from resume for detail view of user ${userId}`);
          }
        } else if (userProfileData?.resumeExtractedText && typeof userProfileData.resumeExtractedText === 'string') {
          // Try to extract from resume text as backup
          const text = userProfileData.resumeExtractedText;
          const emailMatch = text.match(/([A-Za-z]+[A-Za-z\s]*[A-Za-z]+)\s*[:\-]?\s*[\w\.-]+@[\w\.-]+\.\w+/);
          if (emailMatch && emailMatch[1]) {
            const potentialName = emailMatch[1].trim();
            if (potentialName.split(' ').length >= 2 && potentialName.length <= 50) {
              displayName = potentialName;
              console.log(`Extracted name "${displayName}" from resume text for detail view of user ${userId}`);
            }
          }
        }
        
        // If still no name found, try email prefix as fallback
        if (displayName === 'Name not available') {
          const email = userProfileData?.basicInfo?.email || userProfileData?.email;
          if (email) {
            const emailName = email.split('@')[0];
            displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
          }
        }
      }

      // Collect company and admin details for Activity Tab
      let companyDetails = {};
      let adminDetails = {};
      
      // Get unique company IDs from the user data
      if (userProfileData) {
        // Get unique companyIds from companyStatusHistory
        const companyIds = new Set();
        if (userProfileData.companyStatusHistory) {
          Object.keys(userProfileData.companyStatusHistory).forEach(companyId => {
            companyIds.add(companyId);
          });
        }
        
        // Fetch details for each company
        if (companyIds.size > 0) {
          console.log(`Fetching details for ${companyIds.size} companies`);
          
          await Promise.all(Array.from(companyIds).map(async (companyId) => {
            try {
              const companyDoc = await getDoc(doc(db, 'companies', companyId));
              if (companyDoc.exists()) {
                const companyData = companyDoc.data();
                companyDetails[companyId] = {
                  name: companyData.name || 'Unknown Company',
                  logo: companyData.logo || null
                };
              }
            } catch (error) {
              console.error(`Error fetching details for company ${companyId}:`, error);
            }
          }));
        }
        
        // Get unique admin UIDs from companyStatusHistory and contactOutreach
        const adminUids = new Set();
        
        // From companyStatusHistory
        if (userProfileData.companyStatusHistory) {
          Object.values(userProfileData.companyStatusHistory).forEach(historyArray => {
            if (Array.isArray(historyArray)) {
              historyArray.forEach(event => {
                if (event.adminUid) {
                  adminUids.add(event.adminUid);
                }
              });
            }
          });
        }
        
        // From contactOutreach
        if (userProfileData.contactOutreach) {
          Object.values(userProfileData.contactOutreach).forEach(outreach => {
            if (outreach.contactedByAdminUid) {
              adminUids.add(outreach.contactedByAdminUid);
            }
          });
        }
        
        // Fetch details for each admin
        if (adminUids.size > 0) {
          console.log(`Fetching details for ${adminUids.size} admins`);
          
          await Promise.all(Array.from(adminUids).map(async (adminUid) => {
            try {
              const adminDoc = await getDoc(doc(db, 'user_profiles', adminUid));
              if (adminDoc.exists()) {
                const adminData = adminDoc.data();
                adminDetails[adminUid] = {
                  firstName: adminData.basicInfo?.firstName || adminData.displayName || 'Admin',
                  photoURL: adminData.photoURL || null
                };
              }
            } catch (error) {
              console.error(`Error fetching details for admin ${adminUid}:`, error);
            }
          }));
        }
      }
      
      // Combine all data
      const userDetailData = {
        ...userProfileData,
        id: userId,
        displayName: displayName,
        email: userProfileData?.basicInfo?.email || userProfileData?.email || waitlistData?.userInfo?.email || 'Unknown Email',
        // Add profile summary data if available
        profileSummary: profileSummaryData?.profileSummary || '',
        keySkills: profileSummaryData?.keySkills || [],
        painPoints: profileSummaryData?.painPoints || [],
        // Add waitlist info if available
        waitlistData: waitlistData,
        submissionId: submissionId,
        // Company and admin details for Activity Tab
        _companyDetails: companyDetails,
        _adminDetails: adminDetails,
        // If fields exist
        hasUserProfile: !!userProfileData,
        hasProfileSummary: !!profileSummaryData,
        hasWaitlistData: !!waitlistData
      };
      
      setSelectedUserData(userDetailData);
    } catch (error) {
      console.error("Error fetching user details:", error);
      toast.error(`Failed to load user details: ${error.message}`);
      setSelectedUserData(null);
    } finally {
      setLoadingUserDetail(false);
    }
  };

  // Close user detail view and refresh candidates if needed
  const handleCloseUserDetail = (deleted = false) => {
    setSelectedUserId(null);
    setSelectedUserData(null);
    
    // If a user was deleted, refresh the list
    if (deleted) {
      // Refresh candidates data
      fetchCandidates();
    }
  };

  // Handle archive for multiple selected users
  const handleArchiveSelected = async () => {
    if (selectedUserIds.length === 0) {
      toast.error('No users selected');
      return;
    }

    // Confirm with the user
    if (!window.confirm(`Are you sure you want to archive ${selectedUserIds.length} user${selectedUserIds.length > 1 ? 's' : ''}? This action cannot be undone.`)) {
      return;
    }

    setArchiving(true);
    
    try {
      const results = await Promise.allSettled(
        selectedUserIds.map(userId => archiveUserAccount(userId))
      );
      
      // Count successes and failures
      const successes = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      const failures = results.length - successes;
      
      if (failures === 0) {
        toast.success(`Successfully archived ${successes} user${successes !== 1 ? 's' : ''}`);
      } else if (successes === 0) {
        toast.error(`Failed to archive ${failures} user${failures !== 1 ? 's' : ''}`);
      } else {
        toast.success(`Archived ${successes} user${successes !== 1 ? 's' : ''}, but failed to archive ${failures} user${failures !== 1 ? 's' : ''}`);
      }
      
      // Clear selection and refresh candidates
      setSelectedUserIds([]);
      fetchCandidates();
    } catch (error) {
      console.error('Error archiving users:', error);
      toast.error(`Error archiving users: ${error.message}`);
    } finally {
      setArchiving(false);
    }
  };

  // Handle send reminder emails to selected candidates
  const handleSendReminderEmails = async () => {
    if (selectedUserIds.length === 0) {
      toast.error('No candidates selected');
      return;
    }

    setSendingReminders(true);
    
    try {
      // Track successful and failed emails
      let successCount = 0;
      let failCount = 0;
      
      // Process each selected candidate
      for (const userId of selectedUserIds) {
        // Find the candidate in the candidates array
        const candidate = candidates.find(c => c.id === userId);
        
        if (!candidate) {
          console.error(`Candidate with ID ${userId} not found in candidates list`);
          failCount++;
          continue;
        }
        
        if (!candidate.email) {
          console.error(`Candidate ${userId} does not have an email address`);
          failCount++;
          continue;
        }
        
        // Prepare the email content
        const recipientName = candidate.displayName || 
                             (candidate.basicInfo && candidate.basicInfo.firstName) || 
                             'there';
        
        // Extract first name for the greeting
        const firstName = recipientName.split(' ')[0];
        
        const emailData = {
          to: candidate.email,
          from: {
            name: "Grery Weissend",
            email: "grey@justburnt.com"
          },
          subject: 'Burnt Reminder 🔥: Connect to employees at top companies now',
          text: `Hi ${firstName},

Thanks for joining Burnt! There are 100+ people like you who have taken the first step to find their next role.    

We've onboarded employees at 40+ companies including Meta, Airtable, Lyft, and DoorDash. 

Match with a connection to help inform your career's next step: https://www.justburnt.com/

Best,
Grey Weissend`
        };
        
        try {
          // Send the email using the callable function
          const result = await sendAppEmailCallable(emailData);
          console.log(`Email sent to ${candidate.email}:`, result.data);
          successCount++;
        } catch (error) {
          console.error(`Error sending email to ${candidate.email}:`, error);
          failCount++;
          // Don't throw, keep trying other emails
        }
      }
      
      // Show summary toast
      if (failCount === 0) {
        toast.success(`Successfully sent reminder emails to ${successCount} candidate${successCount !== 1 ? 's' : ''}`);
        // Clear selection after successful operation
        setSelectedUserIds([]);
      } else if (successCount === 0) {
        toast.error(`Failed to send reminder emails to ${failCount} candidate${failCount !== 1 ? 's' : ''}`);
      } else {
        toast.success(`Sent reminder emails to ${successCount} candidate${successCount !== 1 ? 's' : ''}, but failed for ${failCount} candidate${failCount !== 1 ? 's' : ''}`);
      }
      
    } catch (error) {
      console.error('Error sending reminder emails:', error);
      toast.error(`Error sending reminder emails: ${error.message}`);
    } finally {
      setSendingReminders(false);
    }
  };

  // Handle send welcome emails to selected candidates
  const handleSendWelcomeEmails = async () => {
    if (selectedUserIds.length === 0) {
      toast.error('No candidates selected');
      return;
    }

    setSendingWelcomeEmails(true);
    
    try {
      // Track successful and failed emails
      let successCount = 0;
      let failCount = 0;
      
      // Get Firebase Functions instance
      const functions = getFunctions();
      const sendWelcomeEmail = httpsCallable(functions, 'sendWelcomeEmail');
      
      // Process each selected candidate
      for (const userId of selectedUserIds) {
        // Find the candidate in the candidates array
        const candidate = candidates.find(c => c.id === userId);
        
        if (!candidate) {
          console.error(`Candidate with ID ${userId} not found in candidates list`);
          failCount++;
          continue;
        }
        
        if (!candidate.email) {
          console.error(`Candidate ${userId} does not have an email address`);
          failCount++;
          continue;
        }
        
        // Get the candidate's first name
        const firstName = (candidate.basicInfo && candidate.basicInfo.firstName) || 
                         candidate.firstName || 
                         candidate.displayName?.split(' ')[0] || 
                         'there';
        
        try {
          // Send the welcome email using the callable function
          const result = await sendWelcomeEmail({
            email: candidate.email,
            firstName: firstName
          });
          
          console.log(`Welcome email sent to ${candidate.email}:`, result.data);
          successCount++;
        } catch (error) {
          console.error(`Error sending welcome email to ${candidate.email}:`, error);
          failCount++;
          // Don't throw, keep trying other emails
        }
      }
      
      // Show summary toast
      if (failCount === 0) {
        toast.success(`Successfully sent welcome emails to ${successCount} candidate${successCount !== 1 ? 's' : ''}`);
        // Clear selection after successful operation
        setSelectedUserIds([]);
      } else if (successCount === 0) {
        toast.error(`Failed to send welcome emails to ${failCount} candidate${failCount !== 1 ? 's' : ''}`);
      } else {
        toast.success(`Sent welcome emails to ${successCount} candidate${successCount !== 1 ? 's' : ''}, but failed for ${failCount} candidate${failCount !== 1 ? 's' : ''}`);
      }
      
    } catch (error) {
      console.error('Error sending welcome emails:', error);
      toast.error(`Error sending welcome emails: ${error.message}`);
    } finally {
      setSendingWelcomeEmails(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Always show the list, regardless of whether a candidate is selected */}
      {/* Action toolbar */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Candidate Progress</h2>
          
          {/* Show Archived Users Toggle */}
          {!showArchivedUsers ? (
            <button
              onClick={() => setShowArchivedUsers(true)}
              className="inline-flex items-center px-3 py-1.5 border border-amber-600 text-xs font-medium rounded-md text-amber-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
            >
              Show Archived
            </button>
          ) : (
            <span className="inline-flex items-center rounded-full bg-amber-100 py-1 pl-3 pr-1 text-xs font-medium text-amber-700 border border-amber-300">
              Showing Archived
              <button
                type="button"
                onClick={() => setShowArchivedUsers(false)}
                className="ml-1 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-amber-400 hover:bg-amber-200 hover:text-amber-500 focus:bg-amber-500 focus:text-white focus:outline-none"
              >
                <svg className="h-2.5 w-2.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </span>
          )}
        </div>
        
        <div className="flex space-x-2">
          {selectedUserIds.length > 0 && (
            <>
              <button
                onClick={handleSendWelcomeEmails}
                disabled={sendingWelcomeEmails}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                {sendingWelcomeEmails ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending Welcome Emails...
                  </>
                ) : (
                  `Send Welcome Email${selectedUserIds.length > 1 ? 's' : ''}`
                )}
              </button>
              
              <button
                onClick={handleSendReminderEmails}
                disabled={sendingReminders}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {sendingReminders ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending Reminders...
                  </>
                ) : (
                  `Send Email Reminder${selectedUserIds.length > 1 ? 's' : ''}`
                )}
              </button>
              
              <button
                onClick={handleArchiveSelected}
                disabled={archiving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {archiving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Archiving...
                  </>
                ) : (
                  `Archive ${selectedUserIds.length} Selected`
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Candidate table */}
      <CandidateProgressTable 
        candidates={candidates} 
        loading={loading} 
        onCandidateClick={handleCandidateClick}
        selectedIds={selectedUserIds}
        onSelectIds={setSelectedUserIds}
      />

      {/* Show the detail view as an overlay when a user is selected */}
      {selectedUserId && selectedUserData && (
        <CandidateDetailView 
          user={selectedUserData} 
          isLoading={loadingUserDetail} 
          onClose={handleCloseUserDetail}
        />
      )}
    </div>
  );
} 